import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Box } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { App as CapApp } from '@capacitor/app';
import {
    isBiometricAvailable,
    authenticateWithBiometrics,
    getBiometricLabel,
    getBiometricOptIn,
} from '../platform/biometric';
import { isNative } from '../platform/env';

// Lock after 5 minutes of background inactivity.
const LOCK_TIMEOUT_MS = 5 * 60 * 1000;

const BiometricLockScreen = ({ children }) => {
    const isLoggedIn = useSelector((s) => s.userInfoReducer.isLoggedIn);
    const [available, setAvailable] = useState(null);
    const [locked, setLocked] = useState(false);
    const [label, setLabel] = useState('Face ID');
    const authenticatingRef = useRef(false);
    const backgroundedAtRef = useRef(null);
    const initialCheckDone = useRef(false);

    // On mount: figure out if biometric is opted in + available, and if so,
    // lock immediately on cold start (ensures Face ID prompt every time the
    // app is freshly opened with a session).
    useEffect(() => {
        if (!isNative) {
            setAvailable(false);
            return;
        }
        let cancelled = false;
        (async () => {
            const avail = await isBiometricAvailable();
            const optedIn = await getBiometricOptIn();
            if (cancelled) return;
            const ready = avail && optedIn;
            setAvailable(ready);
            if (ready) {
                const l = await getBiometricLabel();
                if (!cancelled) setLabel(l);
                if (isLoggedIn && !initialCheckDone.current) {
                    initialCheckDone.current = true;
                    setLocked(true);
                }
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn]);

    // 5-min idle lock on background→foreground cycle.
    useEffect(() => {
        if (!isNative || !available) return;
        let listenerHandle;
        CapApp.addListener('appStateChange', ({ isActive }) => {
            if (!isActive) {
                if (!authenticatingRef.current) {
                    backgroundedAtRef.current = Date.now();
                }
            } else {
                if (backgroundedAtRef.current && isLoggedIn) {
                    const elapsed = Date.now() - backgroundedAtRef.current;
                    if (elapsed >= LOCK_TIMEOUT_MS) {
                        setLocked(true);
                    }
                }
                backgroundedAtRef.current = null;
            }
        }).then((h) => { listenerHandle = h; });

        return () => {
            listenerHandle?.remove?.();
        };
    }, [available, isLoggedIn]);

    const promptBiometrics = useCallback(async () => {
        if (authenticatingRef.current) return;
        authenticatingRef.current = true;
        try {
            const ok = await authenticateWithBiometrics(`Unlock Advice EV`);
            if (ok) setLocked(false);
        } finally {
            authenticatingRef.current = false;
        }
    }, []);

    // Auto-prompt biometric whenever we transition into locked state.
    useEffect(() => {
        if (!locked || !available || !isLoggedIn || authenticatingRef.current) return;
        const timer = setTimeout(() => promptBiometrics(), 300);
        return () => clearTimeout(timer);
    }, [locked, available, isLoggedIn, promptBiometrics]);

    // Still resolving — show a branded "loading" placeholder instead of a
    // blank box so the boot transition between the native splash and the
    // first React paint stays visually consistent. The logo is anchored at
    // exact screen center (matching the splash position), with the spinner
    // and "טוען..." text placed BELOW it via absolute positioning so they
    // don't shift the logo's location during the splash → React handoff.
    if (available === null && isNative) {
        return (
            <Box
                style={{
                    position: 'fixed',
                    inset: 0,
                    background: '#FAFAFA',
                }}
            >
                <img
                    src="/assets/svg/adviceLogo.svg"
                    alt="Advice EV"
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 140,
                        height: 'auto',
                        objectFit: 'contain',
                    }}
                />
                <Box
                    style={{
                        position: 'absolute',
                        top: 'calc(50% + 100px)',
                        left: 0,
                        right: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 16,
                    }}
                >
                    <Box style={{ width: 24, height: 24, border: '3px solid rgba(0,84,150,0.2)', borderTopColor: '#005496', borderRadius: '50%', animation: 'adviceSpin 0.9s linear infinite' }} />
                    <Box style={{ color: 'rgba(14, 21, 36, 0.65)', fontSize: 14 }}>
                        טוען...
                    </Box>
                </Box>
                <style>{`@keyframes adviceSpin { to { transform: rotate(360deg); } }`}</style>
            </Box>
        );
    }

    // Not native, biometric not opted in, no session, or unlocked → render app.
    if (!isNative || !available || !isLoggedIn || !locked) {
        return <>{children}</>;
    }

    return (
        <Box
            role="dialog"
            aria-modal="true"
            aria-label="Advice EV is locked"
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 2147483000,
                background: '#0E1524',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 24,
                paddingTop: 'calc(env(safe-area-inset-top, 0px) + 24px)',
                paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)',
            }}
        >
            <Box
                style={{
                    width: 80,
                    height: 80,
                    borderRadius: 40,
                    background: 'rgba(255,255,255,0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 24,
                }}
            >
                <LockIcon style={{ color: '#FFFFFF', fontSize: 40 }} />
            </Box>
            <Box style={{ color: '#FFFFFF', fontSize: 22, fontWeight: 700, marginBottom: 4, textAlign: 'center' }}>
                Advice EV
            </Box>
            <Box style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, marginBottom: 32, textAlign: 'center' }}>
                {`Unlock with ${label} to continue`}
            </Box>
            <Box
                role="button"
                tabIndex={0}
                onClick={promptBiometrics}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') promptBiometrics(); }}
                style={{
                    background: 'rgba(255,255,255,0.15)',
                    color: '#FFFFFF',
                    fontSize: 16,
                    fontWeight: 500,
                    padding: '14px 32px',
                    borderRadius: 12,
                    cursor: 'pointer',
                    userSelect: 'none',
                }}
            >
                {`Use ${label}`}
            </Box>
        </Box>
    );
};

export default BiometricLockScreen;
